/**
 * @fileoverview Date widget — ISO YYYY-MM-DD.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Render <input type="date"> with optional min/max + 'today' sentinel
 *   resolution on mount (defaultValue and extras.min both honor 'today').
 * - Compute min/max from offset-days configuration (minDateOffsetDays /
 *   maxDateOffsetDays) stored by the template builder.
 * - Validate year range to prevent nonsensical dates (e.g. year 275760).
 *
 * @component DateField
 */
import { useEffect, useMemo, useState } from 'react';
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';
import { resolveFieldDefault } from './fieldDefaults';

/** Extras shape stored by the template-builder PropertiesPanel for date fields. */
interface DateExtras {
  min?: string;
  max?: string;
  defaultToToday?: boolean;
  minDateOffsetDays?: number;
  maxDateOffsetDays?: number;
  disableWeekends?: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');

function resolveDateSentinel(raw: string | undefined): string | undefined {
  if (raw === 'today') return new Date().toISOString().slice(0, 10);
  return raw;
}

/** Compute a local date string offset from today by `days`. */
function dateOffsetFromToday(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Validate year range — reject dates with unreasonable years. */
function isValidDateYear(value: string): boolean {
  const year = parseInt(value.slice(0, 4), 10);
  return !isNaN(year) && year >= 1900 && year <= 2100;
}

/** Check whether a YYYY-MM-DD date string falls on a Saturday or Sunday. */
function isWeekend(iso: string): boolean {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return false;
  const day = new Date(y, m - 1, d).getDay();
  return day === 0 || day === 6; // Sunday=0, Saturday=6
}

/** Format an ISO date (YYYY-MM-DD) into the user's locale (e.g. "5/15/2026"). */
function formatDateLocale(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  try {
    return new Date(y, m - 1, d).toLocaleDateString();
  } catch {
    return iso;
  }
}

export function DateField(props: BaseWidgetProps<string | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, errorMessage, requiredUnmet } = props;
  const extras = (field.extras ?? {}) as DateExtras;

  // Compute effective min/max: offset-based takes precedence over raw string.
  const effectiveMin = useMemo(() => {
    if (typeof extras.minDateOffsetDays === 'number') return dateOffsetFromToday(extras.minDateOffsetDays);
    return resolveDateSentinel(extras.min);
  }, [extras.minDateOffsetDays, extras.min]);

  const effectiveMax = useMemo(() => {
    if (typeof extras.maxDateOffsetDays === 'number') return dateOffsetFromToday(extras.maxDateOffsetDays);
    return resolveDateSentinel(extras.max);
  }, [extras.maxDateOffsetDays, extras.max]);

  // Resolve `defaultValue === 'today'` at mount (B17). Persisted-state has
  // priority — sentinel only fires when value is absent.
  // When disableWeekends is active and the resolved date falls on a weekend,
  // advance to the next Monday so the default is always valid.
  useEffect(() => {
    if (value != null && value !== '') return;
    const dv = resolveFieldDefault(field);
    if (dv === 'today') {
      let today = new Date().toISOString().slice(0, 10);
      if (extras.disableWeekends && isWeekend(today)) {
        const d = new Date();
        while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
        today = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      }
      onChange(today);
    } else if (typeof dv === 'string' && dv) {
      onChange(dv);
    }

  }, [field.id]);

  const [weekendWarning, setWeekendWarning] = useState(false);

  /** Validated onChange handler. */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      setWeekendWarning(false);
      onChange(null);
      return;
    }
    // Reject invalid years (e.g. year 275760 from keyboard entry)
    if (!isValidDateYear(raw)) return;
    // Enforce min/max bounds (soft clamp — browser may not enforce on keyboard entry)
    if (effectiveMin && raw < effectiveMin) return;
    if (effectiveMax && raw > effectiveMax) return;
    // Reject weekends when disableWeekends is configured
    if (extras.disableWeekends && isWeekend(raw)) {
      setWeekendWarning(true);
      return;
    }
    setWeekendWarning(false);
    onChange(raw);
  };

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="date"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      errorMessage={errorMessage}
      requiredUnmet={requiredUnmet}
    >
      <input
        id={field.id}
        type="date"
        value={value ?? ''}
        onChange={handleChange}
        min={effectiveMin}
        max={effectiveMax}
        disabled={!!resolved?.disabled}
        aria-required={!!(field.required || resolved?.required)}
        aria-invalid={!!errorMessage || weekendWarning}
        data-formatted={value ? formatDateLocale(value) : ''}
        className={`w-full px-3 py-2 text-sm border rounded outline-hidden focus:ring-2 focus:ring-blue-300 disabled:opacity-50 ${
          weekendWarning ? 'border-amber-400' : 'border-gray-300'
        }`}
      />
      {weekendWarning && (
        <p className="mt-1 text-xs text-amber-600">Weekends are not allowed. Please select a weekday.</p>
      )}
      {extras.disableWeekends && !weekendWarning && (
        <p className="mt-1 text-[10px] text-gray-400">Weekends are disabled for this field.</p>
      )}
    </FieldWrapper>
  );
}
